import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, styled } from '@mui/material';
import { SvgVBars, ChartLegend } from '@/components/ds/charts';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { useCurrency } from '@/contexts/CurrencyContext';

const StyledChartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  position: 'relative',
  width: '100%',
  minHeight: 500,
}));

// Attestation series colours (parity with the old UI's ATTESTATION_COLORS).
const SERIES_COLORS = {
  attestedActual: '#4E79A7',
  attestedForecast: '#A0B9D8',
  pendingActual: '#FFA500',
  pendingForecast: '#FFCE73',
  rejectedActual: '#E23D3D',
  rejectedForecast: '#F79A92',
};

interface MonthlyBudgetConsumptionChartProps {
  readonly budgetType: 'global' | 'department' | 'system';
  readonly entityId?: number;
  readonly refreshTrigger?: number;
  readonly budgetCurrency?: string;
}

export default function MonthlyBudgetConsumptionChart({
  budgetType,
  entityId,
  refreshTrigger,
  budgetCurrency,
}: Readonly<MonthlyBudgetConsumptionChartProps>) {
  const [chartData, setChartData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { getDisplayCurrency } = useCurrency();
  const displayCurrency = getDisplayCurrency(budgetCurrency);
  const lastRequestKeyRef = useRef<string | null>(null);

  // Measure the card width so the SVG viewBox renders 1:1 with CSS pixels
  // (keeps axis/label fonts at their designed size instead of upscaling).
  const [chartW, setChartW] = useState(640);
  const roRef = useRef<ResizeObserver | null>(null);
  const containerRef = useCallback((el: HTMLDivElement | null) => {
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    if (!el) return;
    const measure = () => setChartW(Math.max(360, el.clientWidth));
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      roRef.current = new ResizeObserver(measure);
      roRef.current.observe(el);
    }
  }, []);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true);
        const params: {
          departmentId?: number | null;
          systemId?: number | null;
          currency?: string;
        } = { currency: displayCurrency };

        if (budgetType === 'department') params.departmentId = entityId ?? null;
        if (budgetType === 'system') params.systemId = entityId ?? null;

        if ((budgetType === 'department' || budgetType === 'system') && !entityId) {
          setChartData(null);
          return;
        }

        const url = urls.DevOps.getMonthlyBudgetConsumptionV2(params);
        const requestKey = `${budgetType}:${entityId ?? 'global'}:${displayCurrency}`;
        if (lastRequestKeyRef.current === requestKey) {
          return;
        }
        lastRequestKeyRef.current = requestKey;

        const data = await fetchWrapper.get(url);
        setChartData(data);
      } catch (error) {
        console.error('Error fetching monthly consumption data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [budgetType, entityId, refreshTrigger, displayCurrency]);

  if (loading || !chartData || !Array.isArray(chartData.monthlyData)) {
    return <div style={{ color: '#8A94A6', fontSize: 13 }}>Loading chart...</div>;
  }

  // Six stacked segments per month (actuals plus forecast-above-actual), like
  // the old UI's stacked bar series. Forecast tooltips show the full forecast.
  const bars = chartData.monthlyData.map((item: any) => {
    const attestedActual = item.attestedActual || 0;
    const pendingActual = item.pendingActual || 0;
    const rejectedActual = item.rejectedActual || 0;
    const attestedForecast = item.attestedForecast || 0;
    const pendingForecast = item.pendingForecast || 0;
    const rejectedForecast = item.rejectedForecast || 0;
    const segments = [
      { v: attestedActual, c: SERIES_COLORS.attestedActual, l: 'Attested Actual' },
      {
        v: Math.max(0, attestedForecast - attestedActual),
        c: SERIES_COLORS.attestedForecast,
        l: 'Attested Forecast',
        tip: formatCurrencyNoUnit(attestedForecast),
      },
      { v: pendingActual, c: SERIES_COLORS.pendingActual, l: 'Pending Actual' },
      {
        v: Math.max(0, pendingForecast - pendingActual),
        c: SERIES_COLORS.pendingForecast,
        l: 'Pending Forecast',
        tip: formatCurrencyNoUnit(pendingForecast),
      },
      { v: rejectedActual, c: SERIES_COLORS.rejectedActual, l: 'Rejected Actual' },
      {
        v: Math.max(0, rejectedForecast - rejectedActual),
        c: SERIES_COLORS.rejectedForecast,
        l: 'Rejected Forecast',
        tip: formatCurrencyNoUnit(rejectedForecast),
      },
    ];
    return {
      v: segments.reduce((sum, s) => sum + s.v, 0),
      l: item.month,
      segments,
    };
  });

  const budgetValues = chartData.monthlyData.map((item: any) => item.monthlyBudget ?? 0);
  const maxValue = Math.max(...bars.map((b: { v: number }) => b.v), ...budgetValues, 1);

  return (
    <StyledChartContainer ref={containerRef}>
      <SvgVBars
        data={bars}
        w={chartW}
        h={500}
        max={maxValue + maxValue * 0.1}
        fmt={formatCurrencyNoUnit}
        refValues={budgetValues}
        refLabel="Monthly Budget"
        rotateLabels={false}
      />
      <Box sx={{ mt: 1.5, width: '100%' }}>
        <ChartLegend
          direction="row"
          items={[
            { color: SERIES_COLORS.attestedActual, name: 'Attested Actual' },
            { color: SERIES_COLORS.attestedForecast, name: 'Attested Forecast' },
            { color: SERIES_COLORS.pendingActual, name: 'Pending Actual' },
            { color: SERIES_COLORS.pendingForecast, name: 'Pending Forecast' },
            { color: SERIES_COLORS.rejectedActual, name: 'Rejected Actual' },
            { color: SERIES_COLORS.rejectedForecast, name: 'Rejected Forecast' },
            { color: '#E23D3D', name: 'Monthly Budget' },
          ]}
        />
      </Box>
    </StyledChartContainer>
  );
}
